import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/common/PageContainer';
import { DataTable } from '@/components/common/DataTable';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useApi } from '@/hooks/useApi';
import { useToast } from '@/hooks/use-toast';
import { Route } from '@/types/route.types';
import { AddRouteModal, EditRouteModal } from '@/components/features/routes';
import { RouteFilterSidebar, RouteFilterValues } from '@/components/features/routes/RouteFilterSidebar';
import { Plus, ArrowLeft, Pencil, Trash2, Route as RouteIcon, Eye, Filter, ChevronRight } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useI18n } from '@/contexts/I18nContext';

const initialFilters: RouteFilterValues = { search: '', from_zone_id: '', to_zone_id: '', is_active: '' };

export default function RoutesList() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { get, delete: deleteRoute, loading } = useApi();
  const { tp } = useI18n();

  const [routes, setRoutes] = useState<Route[]>([]);
  const [filters, setFilters] = useState<RouteFilterValues>(initialFilters);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null);
  const [routeToDelete, setRouteToDelete] = useState<Route | null>(null);

  const fetchRoutes = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (filters.from_zone_id) params.from_zone_id = filters.from_zone_id;
      if (filters.to_zone_id) params.to_zone_id = filters.to_zone_id;
      if (filters.is_active) params.is_active = filters.is_active;
      const response: any = await get('/api/v1/settings/routes', params);
      const data = Array.isArray(response) ? response : response?.data || response?.list || [];
      setRoutes(data);
    } catch (error) { console.error('Error fetching routes:', error); }
  }, [filters.from_zone_id, filters.to_zone_id, filters.is_active]);

  useEffect(() => { fetchRoutes(); }, [fetchRoutes]);

  const handleDeleteRoute = async () => {
    if (!routeToDelete) return;
    try {
      await deleteRoute(`/api/v1/settings/routes/${routeToDelete.id}`);
      toast({ title: tp.routes.deleteSuccess });
      setRouteToDelete(null);
      fetchRoutes();
    } catch (error: any) {
      toast({ title: tp.deliveryZones.errorTitle, description: error?.response?.data?.message || tp.routes.deleteError, variant: 'destructive' });
    }
  };

  const handleEdit = (route: Route) => { setSelectedRoute(route); setShowEditModal(true); };
  const handleFilterChange = (key: keyof RouteFilterValues, value: string) => setFilters((prev) => ({ ...prev, [key]: value === 'all' ? '' : value }));
  const handleClearFilters = () => setFilters(initialFilters);

  const filteredRoutes = routes.filter((route) => {
    if (!filters.search) return true;
    const query = filters.search.toLowerCase();
    return route.code?.toLowerCase().includes(query) || route.name?.toLowerCase().includes(query) || route.from_zone?.name?.toLowerCase().includes(query) || route.to_zone?.name?.toLowerCase().includes(query);
  });

  const columns = [
    { header: tp.routes.code, accessor: (route: Route) => <span className="font-mono text-sm font-medium">{route.code}</span> },
    { header: tp.routes.routeName, accessor: (route: Route) => <span className="font-medium">{route.name}</span> },
    { header: tp.routes.zone, accessor: (route: Route) => <div className="flex items-center gap-2 text-sm"><span>{route.from_zone?.name || '-'}</span><ArrowLeft className="h-4 w-4 text-muted-foreground" /><span>{route.to_zone?.name || '-'}</span></div> },
    { header: tp.routes.distanceTime, accessor: (route: Route) => <div className="text-sm text-muted-foreground">{route.estimated_distance_km && <span>{route.estimated_distance_km} {tp.routes.km}</span>}{route.estimated_distance_km && route.estimated_time_minutes && ' • '}{route.estimated_time_minutes && <span>{route.estimated_time_minutes} {tp.routes.minutes}</span>}{!route.estimated_distance_km && !route.estimated_time_minutes && '-'}</div> },
    { header: tp.routes.status, accessor: (route: Route) => <StatusBadge status={route.is_active ? tp.routes.active : tp.routes.inactive} /> },
  ];

  const renderActions = (route: Route) => (
    <>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/dashboard/settings/routes/${route.id}`)} title={tp.routes.viewDetails}><Eye className="h-4 w-4" /></Button>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(route)} title={tp.routes.edit}><Pencil className="h-4 w-4" /></Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setRouteToDelete(route)} title={tp.routes.delete}><Trash2 className="h-4 w-4" /></Button>
    </>
  );

  const FilterSidebarContent = <RouteFilterSidebar filters={filters} onFilterChange={handleFilterChange} onClearFilters={handleClearFilters} />;

  return (
    <PageContainer>
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ChevronRight className="h-5 w-5" /></Button>
            <div><h1 className="text-xl md:text-2xl font-bold">{tp.routes.listTitle}</h1><p className="text-sm text-muted-foreground">{tp.routes.listSubtitle}</p></div>
          </div>
          <div className="flex gap-2">
            <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
              <SheetTrigger asChild><Button variant="outline" size="icon" className="lg:hidden"><Filter className="h-4 w-4" /></Button></SheetTrigger>
              <SheetContent side="right" className="w-72 p-4"><div className="mt-6">{FilterSidebarContent}</div></SheetContent>
            </Sheet>
            <Button onClick={() => setShowAddModal(true)} className="gap-2"><Plus className="h-4 w-4" />{tp.routes.addNewRoute}</Button>
          </div>
        </div>
      </div>
      <div className="flex gap-4 h-[calc(100vh-180px)]">
        <div className="hidden lg:block h-full overflow-y-auto">{FilterSidebarContent}</div>
        <div className="flex-1 min-w-0">
          {loading && !routes.length ? <LoadingSkeleton type="table" rows={5} /> : filteredRoutes.length === 0 ? (
            <EmptyState icon={<RouteIcon className="h-16 w-16" />} title={tp.routes.noRoutes} description={tp.routes.noRoutesDesc} action={{ label: tp.routes.addNewRoute, onClick: () => setShowAddModal(true) }} />
          ) : (
            <DataTable data={filteredRoutes} columns={columns} actions={renderActions} selectable={true} selectedRows={selectedRows} onSelectionChange={setSelectedRows} />
          )}
        </div>
      </div>
      <AddRouteModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onSuccess={fetchRoutes} />
      <EditRouteModal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setSelectedRoute(null); }} onSuccess={fetchRoutes} route={selectedRoute} />
      <AlertDialog open={!!routeToDelete} onOpenChange={() => setRouteToDelete(null)}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>{tp.routes.deleteConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>{tp.routes.deleteConfirmDesc.replace('{name}', routeToDelete?.name ?? '')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogCancel>{tp.deliveryZones.cancelButton}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRoute} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">{tp.routes.delete}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
